import React, {useMemo} from "react"
import {extractAvailableSeasons, generateCountByKey, generateSumByKey, getSeason, getSeasonLabel} from "../utils";
import {blue, lightBlue} from "@mui/material/colors";
import HighchartsReact from "highcharts-react-official";
import {useTranslation} from "react-i18next";
import useHighChartLocaleOptions from "../useHighChartLocaleOptions";
import Highcharts, {chartLogoOptions, creditsOptions, exportingOptions} from "../highChartsBase";
import Toolbar, {useToolbar} from "./Toolbar";

const IncidentHistoricalCounts = ({data: originalData}) => {
    const {t} = useTranslation("graphs");
    const highChartLocaleOptions = useHighChartLocaleOptions();

    const attributes = [
        {
            label: t('incidentHistoricalCounts.legend.numberOfPeopleCaught'),
            value: 'numberOfPeopleCaught',
        },
        {
            label: t('incidentHistoricalCounts.legend.numberOfPeopleInjured'),
            value: 'numberOfPeopleInjured'
        },
        {
            label: t('incidentHistoricalCounts.legend.numberOfPeopleUninjured'),
            value: 'numberOfPeopleUninjured',
        },
        {
            label: t('incidentHistoricalCounts.legend.numberOfDeaths'),
            value: 'numberOfDeaths'
        },
        {
            label: t('incidentHistoricalCounts.legend.numberOfIncidents'),
            value: 'numberOfIncidents'
        }
    ]

    const data = useMemo(() => (originalData.map(({date, ...rest}) => {
            return {
                date,
                season: getSeason(date),
                ...rest
            }
        })
    ), [originalData]);

    const availableSeasons = useMemo( () => extractAvailableSeasons(data), [data] );

    const toolbar = useToolbar({
        availableAttributes: attributes,
        initialSelectedAttribute: attributes[0].value,
        availableSeasons
    });
    const attribute = toolbar.selectedAttribute;
    const isCountAttribute = attribute === 'numberOfIncidents';

    // Get the data filtered by the toolbar selections
    const filteredData = useMemo( () => (
        data.filter( item => {
            const itemSeason = item.season;
            return (toolbar.seasonFrom ? itemSeason >= toolbar.seasonFrom : true)  && (toolbar.seasonTo ? itemSeason <= toolbar.seasonTo : true)
        })
    ), [data, toolbar.seasonFrom, toolbar.seasonTo]);

    const filteredSeasons = useMemo( () => extractAvailableSeasons(filteredData), [filteredData] );
    const seasonLabels = filteredSeasons.map(season => getSeasonLabel(season));

    const seasonStat = generateSumByKey(filteredData, [attribute], 'season');
    const countStat = generateCountByKey(filteredData, 'season');
    const allValues = filteredSeasons.map(season => isCountAttribute ? (countStat[season] ?? null) : (seasonStat[season]?.[attribute]?.sum ?? null));
    const valuesMean = allValues.length > 0 ? Math.round(allValues.reduce((acc, value) => acc + value, 0) / allValues.length) : null;

    const subtitle = t('subtitle.seasonFrom', {seasonFrom: getSeasonLabel(toolbar.seasonFrom)}) + (toolbar.seasonTo ? t('subtitle.seasonTo', {seasonTo: getSeasonLabel(toolbar.seasonTo)}) : '');

    const options = {
        ...highChartLocaleOptions,
        exporting: {
            ...exportingOptions.exporting,
            filename: `incident_historical_counts`
        },
        title: {
            text: isCountAttribute ? t('incidentHistoricalCounts.titleIncidents') : t('incidentHistoricalCounts.title') + ': ' + t(`incidentHistoricalCounts.legend.${attribute}`)
        },
        subtitle: {
            text: subtitle
        },
        series: [
            {
                data: allValues,
                type: 'column',
                name: t(`incidentHistoricalCounts.legend.${attribute}`),
                color: lightBlue[500],
                yAxis: 0,
            },
            {
                data: filteredSeasons.map(_ => valuesMean ?? null),
                type: 'line',
                name: t(`incidentHistoricalCounts.legend.${attribute}Mean`),
                color: blue[900],
                yAxis: 0,
            }
        ],
        xAxis: {
            categories: seasonLabels
        },
        yAxis: [
            {
                title: {
                    text: isCountAttribute ? t('incidentHistoricalCounts.numberOfIncidents') : t('incidentHistoricalCounts.numberOfPeople')
                },
                allowDecimals: false,
            },
        ],
        tooltip: {
            shared: true,
            crosshairs: true
        },
        plotOptions: {
            series: {
                marker: {
                    enabled: true,
                    symbol: 'circle',
                },
                line: {
                    connectNulls: false // Do not connect null points
                },
                states: {
                    inactive: {
                        opacity: 1 // Prevent fading by keeping opacity at 1
                    }
                }
            },
            column: {
                dataLabels: {
                    enabled: true,
                    crop: false,
                    overflow: 'none',
                    formatter: function() {
                        return this.y;
                    }
                }
            }
        },
        chart: {
            zoomType: 'x', // Enables horizontal zooming; use 'y' or 'xy' for vertical or both directions
            height: 500,
            ...chartLogoOptions,
        },
        ...creditsOptions,
    }

    return <>
        <Toolbar {...toolbar} />
        <HighchartsReact highcharts={Highcharts} options={options} />
    </>
}

export default IncidentHistoricalCounts